import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";
import { notify } from "./NotifierReducer";
import { getService } from "./service";

const initialState = {
  trainingCertifications: [],
  loading: false,
  error: null,
  total: 0,
};

const FETCH_REQUEST = "TRAINING_CERTIFICATION_FETCH_REQUEST";
const FETCH_SUCCESS = "TRAINING_CERTIFICATION_FETCH_SUCCESS";
const FETCH_FAILURE = "TRAINING_CERTIFICATION_FETCH_FAILURE";
const CREATE_REQUEST = "TRAINING_CERTIFICATION_CREATE_REQUEST";
const CREATE_SUCCESS = "TRAINING_CERTIFICATION_CREATE_SUCCESS";
const CREATE_FAILURE = "TRAINING_CERTIFICATION_CREATE_FAILURE";
const UPDATE_REQUEST = "TRAINING_CERTIFICATION_UPDATE_REQUEST";
const UPDATE_SUCCESS = "TRAINING_CERTIFICATION_UPDATE_SUCCESS";
const UPDATE_FAILURE = "TRAINING_CERTIFICATION_UPDATE_FAILURE";
const DELETE_REQUEST = "TRAINING_CERTIFICATION_DELETE_REQUEST";
const DELETE_SUCCESS = "TRAINING_CERTIFICATION_DELETE_SUCCESS";
const DELETE_FAILURE = "TRAINING_CERTIFICATION_DELETE_FAILURE";

export const getTrainingCertifications =
  (
    page = 0,
    pageSize = 20,
    sortField = "id",
    sortDirection = "asc",
    filters = {},
    userAgency
  ) =>
  async (dispatch) => {
    const service = getService(serviceNames.perTrainingCertification);
    const query = {
      $limit: pageSize,
      $skip: page * pageSize,
      $sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
      isDeleted: false,
      agencyId: userAgency,
    };

    const textFields = [];

    textFields.forEach((field) => {
      const value = filters[field];
      if (value) {
        query[field] = filters.exactMatch ? value : { $like: `%${value}%` };
      }
    });

    const getQueryValue = (input) => {
      if (Array.isArray(input)) {
        const values = input.map((i) => i.value).filter(Boolean);
        return values.length > 0 ? { $in: values } : undefined;
      } else if (input?.value) {
        return input.value;
      }
      return undefined;
    };

    const dropdownFields = ["certificationType", "employee"];

    dropdownFields.forEach((field) => {
      const value = getQueryValue(filters[field]);
      if (value !== undefined) {
        query[field] = value;
      }
    });

    if (Array.isArray(filters.tagTypes) && filters.tagTypes.length > 0) {
      query.tagTypes = filters.tagTypes.map((t) => t.value);
    }

    if (filters.employeeStatus && filters.employeeStatus?.value) {
      query.employeeStatus = filters.employeeStatus.value;
    }

    if (filters.exactMatch !== undefined) {
      query.exactMatch = filters.exactMatch;
    }

    if (filters.filterField?.value && filters.filterValue) {
      const fieldMap = {};
      const field =
        fieldMap[filters.filterField.value] || filters.filterField.value;

      query[field] = filters.exactMatch
        ? filters.filterValue
        : { $like: `%${filters.filterValue}%` };
    }

    if (filters.dateObtainedFrom) {
      query.dateObtained = {
        ...(query.dateObtained || {}),
        $gte: filters.dateObtainedFrom,
      };
    }

    if (filters.dateObtainedTo) {
      query.dateObtained = {
        ...(query.dateObtained || {}),
        $lte: filters.dateObtainedTo,
      };
    }

    if (filters.expireDateFrom) {
      query.expireDate = {
        ...(query.expireDate || {}),
        $gte: filters.expireDateFrom,
      };
    }

    if (filters.expireDateTo) {
      query.expireDate = {
        ...(query.expireDate || {}),
        $lte: filters.expireDateTo,
      };
    }

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_REQUEST,
        success: FETCH_SUCCESS,
        failure: FETCH_FAILURE,
      },
      apiCall: () => service.find({ query }),
      transformResult: (res) => {
        return {
          trainingCertifications: pageSize === -1 ? res : res.data,
          total: pageSize === -1 ? res?.length : res.total,
        };
      },
      errorMessagePrefix: "Failed to fetch training certifications",
    });
  };

export const createTrainingCertification =
  (trainingCertificationData) => async (dispatch) => {
    const service = getService(serviceNames.perTrainingCertification);
    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: CREATE_REQUEST,
        success: CREATE_SUCCESS,
        failure: CREATE_FAILURE,
      },
      apiCall: () => service.create(trainingCertificationData),
      successMessage: "Training Certification created successfully",
      errorMessagePrefix: "Failed to create training certification",
    });
  };

export const createMultipleTrainingCertifications =
  (certificationsArray) => async (dispatch) => {
    const service = getService(serviceNames.perTrainingCertification);

    dispatch({ type: CREATE_REQUEST });

    try {
      const results = await Promise.allSettled(
        certificationsArray.map((data) => service.create(data))
      );

      const successes = results
        .map((res, i) => (res.status === "fulfilled" ? res.value : null))
        .filter(Boolean);

      const hasFailures = results.some((res) => res.status === "rejected");

      if (successes.length > 0) {
        dispatch({
          type: CREATE_SUCCESS,
          payload: successes.length === 1 ? successes[0] : successes,
        });
      }

      if (hasFailures) {
        dispatch({
          type: CREATE_FAILURE,
          payload: "Some training certifications failed to create.",
        });
        dispatch(
          notify(
            "Some or all training certifications failed to create.",
            "error"
          )
        );
      } else {
        dispatch(
          notify("All training certifications created successfully.", "success")
        );
      }

      return successes.length;
    } catch (err) {
      dispatch({
        type: CREATE_FAILURE,
        payload: "Unexpected error during batch creation",
      });
      console.error("Unexpected error during batch creation", err);
    }
  };

export const updateTrainingCertification =
  (id, trainingCertificationData, showSuccessMessage = true) =>
  async (dispatch) => {
    const service = getService(serviceNames.perTrainingCertification);
    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: UPDATE_REQUEST,
        success: UPDATE_SUCCESS,
        failure: UPDATE_FAILURE,
      },
      apiCall: () => service.patch(id, trainingCertificationData),
      successMessage: showSuccessMessage
        ? "Training Certification updated successfully"
        : null,
      errorMessagePrefix: "Failed to update training certification",
    });
  };

export const deleteTrainingCertification = (id, userId) => async (dispatch) => {
  const service = getService(serviceNames.perTrainingCertification);
  const patchData = {
    isDeleted: true,
    deletedBy: userId,
    deletedAt: new Date().toISOString(),
  };

  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: DELETE_REQUEST,
      success: DELETE_SUCCESS,
      failure: DELETE_FAILURE,
    },
    apiCall: () => service.patch(id, patchData),
    transformResult: () => id,
    successMessage: "Training Certification deleted successfully",
    errorMessagePrefix: "Failed to delete training certification",
  });
};

export default function trainingCertificationReducer(
  state = initialState,
  action
) {
  switch (action.type) {
    case FETCH_REQUEST:
    case CREATE_REQUEST:
    case UPDATE_REQUEST:
    case DELETE_REQUEST:
      return { ...state, loading: true, error: null };
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        trainingCertifications: action.payload.trainingCertifications,
        total: action.payload.total,
      };
    case CREATE_SUCCESS:
      const newItems = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      return {
        ...state,
        loading: false,
        trainingCertifications: [...state.trainingCertifications, ...newItems],
        total: state.total + newItems.length,
      };

    case UPDATE_SUCCESS:
      return {
        ...state,
        loading: false,
        trainingCertifications: state.trainingCertifications.map((tc) =>
          tc.id === action.payload.id ? action.payload : tc
        ),
      };
    case DELETE_SUCCESS:
      return {
        ...state,
        loading: false,
        trainingCertifications: state.trainingCertifications.filter(
          (tc) => tc.id !== action.payload
        ),
        total: state.total - 1,
      };
    case FETCH_FAILURE:
    case CREATE_FAILURE:
    case UPDATE_FAILURE:
    case DELETE_FAILURE:
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}
